<template>
  <section class="h-full p-3">
    <div class="shadow p-3 min-h-full mb-12 rounded-lg bg-white">
      <div class="flex justify-between items-center mb-4">
        <h3 class="font-bold mb-3">Store</h3>
        <button
          type="button"
          class="bg-green-light text-white rounded-lg px-4 py-1 hidden md:grid opacity-50"
          @click.prevent="toggleStoreModal"
          disabled
        >
          Add
        </button>
      </div>
      <Modal :title="'Add Store'" v-model="isModalOpen">
        <section>
          <div class="mb-2">
            <label class="block">
              <span>Item Name</span>
              <input
                type="text"
                class="form-input mt-1 block w-full"
                v-model="$v.title.$model"
              />
              <div v-if="$v.title.$error">
                <small class="text-red-500" v-if="!$v.title.required">
                  Item Name field is required
                </small>
                <small class="text-red-500" v-if="!$v.title.maxLength">
                  Item Name must have at most 50 letters.
                </small>
                <small class="text-red-500" v-if="!$v.title.minLength">
                  Item Name must have at least 2 letters.
                </small>
              </div>
            </label>
          </div>
          <div class="mb-2">
            <label class="block">
              <span>Cost</span>
              <input
                type="text"
                class="form-input mt-1 block w-full"
                min="1"
                v-model="$v.cost.$model"
              />
            </label>
            <div v-if="$v.cost.$error">
              <small class="text-red-500" v-if="!$v.cost.required">
                Cost field is required
              </small>
              <small class="text-red-500" v-if="!$v.cost.decimal">
                Accepts positive and negative integers.
              </small>
              <small class="text-red-500" v-if="!$v.cost.maxLength">
                Cost must have at most 6 letters.
              </small>
            </div>
          </div>
          <div class="mb-2">
            <span>Link</span>
            <label class="block">
              <input
                type="text"
                class="form-input mt-1 block w-full"
                v-model="$v.url_link.$model"
              />
            </label>
            <div v-if="$v.url_link.$error">
              <small class="text-red-500" v-if="!$v.url_link.required">
                Link is required
              </small>
              <small class="text-red-500" v-if="!$v.url_link.url">
                Accepts only URLs.
              </small>
            </div>
          </div>
          <div class="mb-2">
            <span>Merchandise Image</span>
            <label class="block">
              <input
                type="file"
                class="form-input mt-1 block w-full"
                @change="handleFileChange"
              />
            </label>
            <div v-if="$v.thumbnailImage.$error">
              <small class="text-red-500" v-if="!$v.thumbnailImage.required">
                File is required
              </small>
            </div>
            <div v-if="thumbnailImagePreview">
              <img
                :src="thumbnailImagePreview"
                alt=""
                @error="defaultImage"
              />
            </div>
          </div>
          <div class="py-2">
            <button
              class="bg-green-600 text-white rounded px-2 py-1"
              @click="handleStoreAdd"
            >
              Add
            </button>
          </div>
        </section>
      </Modal>
      <TrainerMerchandiseCardList
        v-if="
          trainerStoreList.data &&
          trainerStoreList.data.data &&
          trainerStoreList.data.data.length
        "
        v-on:pencil_id="handlePencilId"
        v-on:trash_id="handleTrashId"
        :data="trainerStoreList.data && trainerStoreList.data.data"
      />
      <div v-else>
        <div
          v-if="!loadingStatus"
          class="flex flex-col justify-center items-center text-xl text-gray-700 mt-8 space-y-8"
        >
          <img
            :src="require(`@/assets/svg/store_no_data.svg`)"
            alt="..."
            class="w-72"
          />
          <div class="flex flex-col justify-center items-center text-center">
            <span> Looks like there is nothing here yet.</span>
            <span>Start setting up your store by adding new items here.</span>
          </div>
        </div>
      </div>

      <div
        class="py-4 text-center"
        v-if="trainerStoreList.data && trainerStoreList.data.next_page_url"
      >
        <button
          class="w-32 ring-gray-300 ring-1 rounded"
          @click="
            handleShowMore(
              trainerStoreList.data && trainerStoreList.data.next_page_url
            )
          "
        >
          Show More
        </button>
      </div>
    </div>
    <div
      class="fixed h-12 left-0 bottom-0 bg-white w-full text-center grid md:hidden flex justify-center"
    >
      <button
        type="button"
        class="w-12 bg-green-600 text-white rounded-full"
        @click.prevent="toggleStoreModal"
      >
        <Plus />
      </button>
    </div>
  </section>
</template>

<script>
import { Modal } from "@/components/t-components";
import { Plus } from "@/components/icons";
import TrainerMerchandiseCardList from "@/components/TrainerMerchandiseCardList";
import {
  required,
  url,
  maxLength,
  decimal,
  minLength,
} from "vuelidate/lib/validators";
import { mapGetters, mapActions } from "vuex";

export default {
  components: {
    Modal,
    TrainerMerchandiseCardList,
    Plus,
  },
  data() {
    return {
      id: "",
      title: "",
      cost: "",
      url_link: "",
      thumbnailImage: "",
      thumbnailImagePreview: "",
      isModalOpen: false,
    };
  },
  validations: {
    title: {
      required,
      maxLength: maxLength(50),
      minLength: minLength(2),
    },
    cost: {
      required,
      decimal,
      maxLength: maxLength(6),
    },
    url_link: {
      required,
      url,
    },
    thumbnailImage: {
      required,
    },
  },
  computed: {
    ...mapGetters(["trainerStoreList", "trainerStoreView"]),
    loadingStatus() {
      return this.$store.getters.loadingStatus;
    },
  },
  methods: {
    ...mapActions([
      "getTrainerStoreList",
      "getTrainerStoreListMore",
      "trainerStoreAdd",
      "getTrainerStoreView",
      "trainerStoreUpdate",
      "trainerStoreDelete",
    ]),

    handleFileChange(e) {
      var files = e.target.files || e.dataTransfer.files;

      if (!files.length) {
        return;
      }
      this.thumbnailImage = files[0];
      this.createThumbnailImage(this.thumbnailImage);
    },

    createThumbnailImage(file) {
      var reader = new FileReader();

      reader.onload = (e) => {
        this.thumbnailImagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    async handleStoreAdd() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        console.log("invalid");
      } else {
        let data = new FormData();
        data.append("title", this.title);
        data.append("cost", this.cost);
        data.append("url_link", this.url_link);
        data.append("thumbnail_image", this.thumbnailImage);
        if (this.id) {
          data.append("id", this.id);
          this.trainerStoreUpdate(data);
        } else {
          this.trainerStoreAdd(data);
        }
        this.toggleStoreModal();
      }
      await this.getTrainerStoreList();
    },

    async handlePencilId(id) {
      await this.getTrainerStoreView(id);
      this.id = id;
      this.title = this.trainerStoreView[0].title;
      this.cost = this.trainerStoreView[0].cost;
      this.url_link = this.trainerStoreView[0].url_link;
      this.thumbnailImagePreview = this.baseUrlSrc(
        this.trainerStoreView[0].thumbnail_url
      );
      this.toggleStoreModal();
    },

    handleTrashId(id) {
      var c = confirm("Are you sure?");
      if (c) {
        this.trainerStoreDelete(id);
      }
      this.getTrainerStoreList();
    },

    handleShowMore(str) {
      const params = str.substring(str.indexOf("?"));
      this.getTrainerStoreListMore(params);
    },

    toggleStoreModal() {
      this.isModalOpen = !this.isModalOpen;
    },
  },
  async created() {
    await this.getTrainerStoreList();
  },
};
</script>